import { supabase } from '../lib/supabase';

interface ExchangeRate {
  from_currency: string;
  to_currency: string;
  rate: number;
}

let cachedRates: { [key: string]: number } = {};
let lastFetchTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000;

export const fetchExchangeRates = async (): Promise<void> => {
  const now = Date.now();
  if (now - lastFetchTime < CACHE_DURATION && Object.keys(cachedRates).length > 0) {
    return;
  }

  try {
    const { data, error } = await supabase
      .from('exchange_rates')
      .select('from_currency, to_currency, rate');

    if (error) throw error;

    if (data) {
      cachedRates = {};
      data.forEach((rate: ExchangeRate) => {
        const key = `${rate.from_currency}_${rate.to_currency}`;
        cachedRates[key] = Number(rate.rate);
        const reverseKey = `${rate.to_currency}_${rate.from_currency}`;
        cachedRates[reverseKey] = 1 / Number(rate.rate);
      });
      lastFetchTime = now;
    }
  } catch (err) {
    console.error('Error fetching exchange rates:', err);
  }
};

export const convertCurrency = async (
  amount: number,
  fromCurrency: string,
  toCurrency: string = 'EUR'
): Promise<number> => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  await fetchExchangeRates();

  const key = `${fromCurrency}_${toCurrency}`;
  const rate = cachedRates[key];

  if (!rate) {
    console.warn(`Exchange rate not found for ${fromCurrency} to ${toCurrency}`);
    return amount;
  }

  return amount / rate;
};

export const getExchangeRate = async (
  fromCurrency: string,
  toCurrency: string
): Promise<number | null> => {
  await fetchExchangeRates();
  const key = `${fromCurrency}_${toCurrency}`;
  return cachedRates[key] || null;
};

export const updateExchangeRate = async (
  fromCurrency: string,
  toCurrency: string,
  rate: number,
  userId: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('exchange_rates')
      .update({
        rate,
        updated_at: new Date().toISOString(),
        updated_by: userId
      })
      .eq('from_currency', fromCurrency)
      .eq('to_currency', toCurrency);

    if (error) throw error;

    cachedRates = {};
    lastFetchTime = 0;
    await fetchExchangeRates();

    return true;
  } catch (err) {
    console.error('Error updating exchange rate:', err);
    return false;
  }
};
